import React, { useState, useEffect } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CButton,
  CForm,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CAvatar,
  CBadge,
  CAlert,
  CSpinner,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
  CProgressBar,
  CProgress,
  CInputGroup,
  CInputGroupText,
} from '@coreui/react'
import { CIcon } from '@coreui/icons-react'
import { 
  cilUser, 
  cilSettings, 
  cilCamera, 
  cilCheckCircle, 
  cilLockLocked,
  cilLowVision,
  cilWarning,
  cilX,
} from '@coreui/icons'
import { useAuth } from '../utils/AuthContext'
import { fetchCompleteProfile, updateUserProfile, changePassword } from '../../utils/api'
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa'

const MyProfile = () => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    bio: ''
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [completeProfile, setCompleteProfile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [isLoadingPassword, setIsLoadingPassword] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [passwordMessage, setPasswordMessage] = useState('')
  const [passwordMessageType, setPasswordMessageType] = useState('')
  const [activeTab, setActiveTab] = useState('personal-info')
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: ''
  })

  // Password strength criteria
  const passwordCriteria = [
    { label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { label: 'Contains uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { label: 'Contains lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { label: 'Contains number', test: (pwd) => /[0-9]/.test(pwd) },
    { label: 'Contains special character', test: (pwd) => /[^A-Za-z0-9]/.test(pwd) }
  ]

  // Check password strength
  const checkPasswordStrength = (password) => {
    let score = 0
    let feedback = ''

    if (!password) {
      setPasswordStrength({ score: 0, feedback: '' })
      return
    }

    // Calculate score based on criteria
    const metCriteria = passwordCriteria.filter(criteria => criteria.test(password))
    score = (metCriteria.length / passwordCriteria.length) * 100

    // Set feedback based on score
    if (score < 40) {
      feedback = 'Weak password'
    } else if (score < 80) {
      feedback = 'Moderate password'
    } else {
      feedback = 'Strong password'
    }

    setPasswordStrength({ score, feedback })
  }

  // Update password strength when new password changes
  useEffect(() => {
    checkPasswordStrength(passwordData.newPassword)
  }, [passwordData.newPassword])

  useEffect(() => {
    const loadCompleteProfile = async () => {
      if (user?.id) {
        setIsLoadingProfile(true)
        try {
          const response = await fetchCompleteProfile(user.id)
          if (response.status === 'success') {
            setCompleteProfile(response.data)
            
            // Update form with complete profile data
            const { profile, company } = response.data
            setFormData({
              firstName: profile.firstName || '',
              lastName: profile.lastName || '',
              email: user.email || '',
              phone: profile.phone || '',
              company: company.companyName || '',
              position: company.positionInCompany || (company.contactPerson === `${profile.firstName} ${profile.lastName}` ? 'Contact Person' : ''),
              bio: '' // Bio field not in current database schema
            })
          } else {
            // Fallback to user data if API fails
            setFormData({
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              email: user.email || '',
              phone: user.phone || '',
              company: user.company || '',
              position: user.position || '',
              bio: user.bio || ''
            })
          }
        } catch (error) {
          console.error('Error loading complete profile:', error)
          // Fallback to user data if API fails
          setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: user.phone || '',
            company: user.company || '',
            position: user.position || '',
            bio: user.bio || ''
          })
        } finally {
          setIsLoadingProfile(false)
        }
      }
    }

    loadCompleteProfile()
  }, [user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword) {
      setPasswordMessage('Current password is required')
      setPasswordMessageType('danger')
      return false
    }
    
    if (!passwordData.newPassword) {
      setPasswordMessage('New password is required')
      setPasswordMessageType('danger')
      return false
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordMessage('New password must be at least 8 characters long')
      setPasswordMessageType('danger')
      return false
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('New passwords do not match')
      setPasswordMessageType('danger')
      return false
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordMessage('New password must be different from current password')
      setPasswordMessageType('danger')
      return false
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage('')

    try {
      // Prepare profile data for API call
      const profileData = {
        userId: user.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        company: formData.company,
        position: formData.position,
        bio: formData.bio
      }

      // Make API call to update profile
      const response = await updateUserProfile(profileData)
      
      if (response.status === 'success') {
        setMessage('Profile updated successfully!')
        setMessageType('success')
      } else {
        setMessage(response.message || 'Failed to update profile. Please try again.')
        setMessageType('danger')
      }
    } catch (error) {
      console.error('Profile update error:', error)
      setMessage(error.message || 'Failed to update profile. Please try again.')
      setMessageType('danger')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setPasswordMessage('')
    
    if (!validatePasswordForm()) {
      return
    }
    
    setIsLoadingPassword(true)

    try {
      // Prepare password data for API call
      const passwordChangeData = {
        userId: user.id,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }

      // Make API call to change password
      const response = await changePassword(passwordChangeData)
      
      if (response.status === 'success') {
        setPasswordMessage('Password changed successfully!')
        setPasswordMessageType('success')
        
        // Reset password form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
      } else {
        setPasswordMessage(response.message || 'Failed to change password. Please try again.')
        setPasswordMessageType('danger')
      }
    } catch (error) {
      console.error('Password change error:', error)
      setPasswordMessage(error.message || 'Failed to change password. Please try again.')
      setPasswordMessageType('danger')
    } finally {
      setIsLoadingPassword(false)
    }
  }

  const getInitials = (firstName, lastName, email) => {
    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    }
    if (email) {
      return email.charAt(0).toUpperCase()
    }
    return 'U'
  }

  const getDisplayName = () => {
    if (formData.firstName && formData.lastName) {
      return `${formData.firstName} ${formData.lastName}`
    }
    if (user?.email) {
      return user.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
    return 'Member'
  }

  const getMembershipStatus = () => {
    // TODO: Get actual membership status from user data or API
    return user?.status || 'Active'
  }

  const getMembershipType = () => {
    // TODO: Get actual membership type from user data or API
    return user?.membershipType || 'Standard Member'
  }

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  // Get color for password strength bar
  const getStrengthColor = (score) => {
    if (score < 40) return 'danger'
    if (score < 80) return 'warning'
    return 'success'
  }

  return (
    <>
      <CRow>
        <CCol xs={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <strong>My Profile</strong>
              <small className="text-muted ms-2">Manage your account settings</small>
            </CCardHeader>
            <CCardBody>
              {isLoadingProfile ? (
                <div className="text-center py-5">
                  <CSpinner color="primary" />
                  <p className="text-muted mt-2">Loading profile data...</p>
                </div>
              ) : (
                <>
                  {/* Profile Header */}
                  <CRow className="mb-4">
                    <CCol md={4} className="text-center mb-4">
                      <div className="position-relative d-inline-block">
                        <CAvatar
                          size="xl"
                          style={{ 
                            backgroundColor: '#321fdb', 
                            color: 'white',
                            width: '120px',
                            height: '120px',
                            fontSize: '2rem'
                          }}
                        >
                          {getInitials(formData.firstName, formData.lastName, user?.email)}
                        </CAvatar>
                        <CButton
                          color="primary"
                          size="sm"
                          className="position-absolute bottom-0 end-0 rounded-circle"
                          style={{ width: '32px', height: '32px' }}
                          title="Change profile picture"
                        >
                          <CIcon icon={cilCamera} size="sm" />
                        </CButton>
                      </div>
                      <h4 className="mt-3">{getDisplayName()}</h4>
                      <p className="text-muted">{getMembershipType()}</p>
                      <CBadge 
                        color={getMembershipStatus().toLowerCase() === 'active' ? 'success' : 'warning'} 
                        className="mb-2"
                      >
                        {getMembershipStatus()}
                      </CBadge>
                      {user?.email && (
                        <div className="mt-2">
                          <small className="text-muted d-block">Member since:</small>
                          <small className="text-muted">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </small>
                        </div>
                      )}
                    </CCol>
                    <CCol md={8}>
                      <div className="h-100 d-flex align-items-center">
                        <div>
                          <h5>Welcome back, {getDisplayName()}!</h5>
                          <p className="text-muted">
                            Use the tabs below to manage your personal information and account security settings.
                          </p>
                        </div>
                      </div>
                    </CCol>
                  </CRow>

                  {/* Tabs */}
                  <CTabs activeItemKey={activeTab} onChange={setActiveTab}>
                    <CTabList variant="underline">
                      <CTab itemKey="personal-info">
                        <CIcon icon={cilUser} className="me-2" />
                        Personal Information
                      </CTab>
                      <CTab itemKey="change-password">
                        <CIcon icon={cilLockLocked} className="me-2" />
                        Change Password
                      </CTab>
                    </CTabList>
                    
                    <CTabContent>
                      {/* Personal Information Tab */}
                      <CTabPanel className="py-4" itemKey="personal-info">
                        {message && (
                          <CAlert color={messageType} className="mb-4">
                            <CIcon icon={cilCheckCircle} className="me-2" />
                            {message}
                          </CAlert>
                        )}

                        <CForm onSubmit={handleSubmit}>
                          <CRow className="mb-3">
                            <CCol md={6}>
                              <CFormLabel htmlFor="firstName">First Name</CFormLabel>
                              <CFormInput
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                placeholder="Enter your first name"
                              />
                            </CCol>
                            <CCol md={6}>
                              <CFormLabel htmlFor="lastName">Last Name</CFormLabel>
                              <CFormInput
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                placeholder="Enter your last name"
                              />
                            </CCol>
                          </CRow>
                          
                          <CRow className="mb-3">
                            <CCol md={6}>
                              <CFormLabel htmlFor="email">Email</CFormLabel>
                              <CFormInput
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter your email"
                                disabled // Email should not be editable or require special handling
                              />
                              <small className="text-muted">Contact support to change your email address</small>
                            </CCol>
                            <CCol md={6}>
                              <CFormLabel htmlFor="phone">Phone</CFormLabel>
                              <CFormInput
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="+254 123 456 789"
                              />
                            </CCol>
                          </CRow>
                          
                          <CRow className="mb-3">
                            <CCol md={6}>
                              <CFormLabel htmlFor="company">Company</CFormLabel>
                              <CFormInput
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                placeholder="Your company name"
                              />
                            </CCol>
                            <CCol md={6}>
                              <CFormLabel htmlFor="position">Position</CFormLabel>
                              <CFormInput
                                type="text"
                                id="position"
                                name="position"
                                value={formData.position}
                                onChange={handleInputChange}
                                placeholder="Your position/title"
                              />
                            </CCol>
                          </CRow>
                          
                          <div className="mb-3">
                            <CFormLabel htmlFor="bio">Bio</CFormLabel>
                            <CFormTextarea
                              id="bio"
                              name="bio"
                              rows={4}
                              value={formData.bio}
                              onChange={handleInputChange}
                              placeholder="Tell us about yourself and your professional experience..."
                            />
                          </div>
                          
                          <div className="d-flex gap-2">
                            <CButton 
                              type="submit" 
                              color="primary" 
                              disabled={isLoading}
                            >
                              {isLoading ? (
                                <>
                                  <CSpinner size="sm" className="me-2" />
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <CIcon icon={cilUser} className="me-2" />
                                  Update Profile
                                </>
                              )}
                            </CButton>
                            <CButton 
                              type="button" 
                              color="secondary" 
                              variant="outline"
                              onClick={() => {
                                // Reset form to original user data
                                if (user) {
                                  setFormData({
                                    firstName: user.firstName || '',
                                    lastName: user.lastName || '',
                                    email: user.email || '',
                                    phone: user.phone || '',
                                    company: user.company || '',
                                    position: user.position || '',
                                    bio: user.bio || ''
                                  })
                                }
                                setMessage('')
                              }}
                            >
                              Reset
                            </CButton>
                          </div>
                        </CForm>
                      </CTabPanel>
                      
                      {/* Change Password Tab */}
                      <CTabPanel className="py-4" itemKey="change-password">
                        {passwordMessage && (
                          <CAlert color={passwordMessageType} className="mb-4">
                            <CIcon icon={passwordMessageType === 'success' ? cilCheckCircle : cilWarning} className="me-2" />
                            {passwordMessage}
                          </CAlert>
                        )}

                        <CRow>
                          <CCol md={7}>
                            <CCard className="border-0 shadow-sm">
                              <CCardBody>
                                <h5 className="mb-4">Change Your Password</h5>
                                <CForm onSubmit={handlePasswordSubmit}>
                                  <div className="mb-4">
                                    <CFormLabel htmlFor="currentPassword">Current Password</CFormLabel>
                                    <CInputGroup>
                                      <CFormInput
                                        type={showPasswords.current ? 'text' : 'password'}
                                        id="currentPassword"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter your current password"
                                        required
                                        invalid={passwordMessageType === 'danger' && !passwordData.currentPassword}
                                      />
                                      <CButton
                                        type="button"
                                        color="secondary"
                                        variant="outline"
                                        onClick={() => togglePasswordVisibility('current')}
                                      >
                                        {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                                      </CButton>
                                    </CInputGroup>
                                  </div>
                                  
                                  <div className="mb-4">
                                    <CFormLabel htmlFor="newPassword">New Password</CFormLabel>
                                    <CInputGroup>
                                      <CFormInput
                                        type={showPasswords.new ? 'text' : 'password'}
                                        id="newPassword"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter your new password"
                                        required
                                        invalid={passwordMessageType === 'danger' && !passwordData.newPassword}
                                      />
                                      <CButton
                                        type="button"
                                        color="secondary"
                                        variant="outline"
                                        onClick={() => togglePasswordVisibility('new')}
                                      >
                                        {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                                      </CButton>
                                    </CInputGroup>
                                  </div>
                                  
                                  <div className="mb-4">
                                    <CFormLabel htmlFor="confirmPassword">Confirm New Password</CFormLabel>
                                    <CInputGroup>
                                      <CFormInput
                                        type={showPasswords.confirm ? 'text' : 'password'}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Confirm your new password"
                                        required
                                        invalid={passwordMessageType === 'danger' && !passwordData.confirmPassword}
                                      />
                                      <CButton
                                        type="button"
                                        color="secondary"
                                        variant="outline"
                                        onClick={() => togglePasswordVisibility('confirm')}
                                      >
                                        {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                                      </CButton>
                                    </CInputGroup>
                                    {passwordData.newPassword && passwordData.confirmPassword && (
                                      <small className={passwordData.newPassword === passwordData.confirmPassword ? 'text-success' : 'text-danger'}>
                                        {passwordData.newPassword === passwordData.confirmPassword ? (
                                          <><FaCheck className="me-1" /> Passwords match</>
                                        ) : (
                                          <><FaTimes className="me-1" /> Passwords do not match</>
                                        )}
                                      </small>
                                    )}
                                  </div>
                                  
                                  <div className="d-flex gap-2">
                                    <CButton 
                                      type="submit" 
                                      color="primary" 
                                      disabled={isLoadingPassword || passwordStrength.score < 40}
                                    >
                                      {isLoadingPassword ? (
                                        <>
                                          <CSpinner size="sm" className="me-2" />
                                          Changing...
                                        </>
                                      ) : (
                                        <>
                                          <CIcon icon={cilLockLocked} className="me-2" />
                                          Change Password
                                        </>
                                      )}
                                    </CButton>
                                    <CButton 
                                      type="button" 
                                      color="secondary" 
                                      variant="outline"
                                      onClick={() => {
                                        setPasswordData({
                                          currentPassword: '',
                                          newPassword: '',
                                          confirmPassword: ''
                                        })
                                        setPasswordMessage('')
                                        setPasswordStrength({ score: 0, feedback: '' })
                                      }}
                                    >
                                      Clear
                                    </CButton>
                                  </div>
                                </CForm>
                              </CCardBody>
                            </CCard>
                          </CCol>
                          
                          <CCol md={5}>
                            {/* Password Strength Section */}
                            <CCard className="border-0 shadow-sm mb-4">
                              <CCardBody>
                                <h5 className="mb-3">Password Strength</h5>
                                {passwordData.newPassword ? (
                                  <>
                                    <CProgress className="mb-2" height={8}>
                                      <CProgressBar
                                        color={getStrengthColor(passwordStrength.score)}
                                        value={passwordStrength.score}
                                      />
                                    </CProgress>
                                    <p className={`mb-3 text-${getStrengthColor(passwordStrength.score)}`}>
                                      <strong>{passwordStrength.feedback}</strong>
                                    </p>
                                    
                                    <div className="password-criteria">
                                      <small className="text-muted d-block mb-2">Password Requirements:</small>
                                      <div className="d-flex flex-column gap-2">
                                        {passwordCriteria.map((criteria, index) => (
                                          <div 
                                            key={index} 
                                            className={`d-flex align-items-center small ${
                                              criteria.test(passwordData.newPassword) ? 'text-success' : 'text-muted'
                                            }`}
                                          >
                                            {criteria.test(passwordData.newPassword) ? (
                                              <FaCheck className="me-2" />
                                            ) : (
                                              <FaTimes className="me-2" />
                                            )}
                                            {criteria.label}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-muted mb-0">
                                    Enter a new password to see its strength
                                  </p>
                                )}
                              </CCardBody>
                            </CCard>

                            {/* Security Tips Section */}
                            <CCard className="border-0 shadow-sm">
                              <CCardBody className="bg-light">
                                <div className="text-center">
                                  <CIcon icon={cilLockLocked} size="3xl" className="mb-3 text-primary" />
                                  <h5 className="text-primary">Password Security Tips</h5>
                                  <ul className="text-start text-muted mt-3">
                                    <li>Use a unique password for each account</li>
                                    <li>Avoid using personal information</li>
                                    <li>Consider using a password manager</li>
                                    <li>Change your password periodically</li>
                                    <li>Never share your password with others</li>
                                  </ul>
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                        </CRow>
                      </CTabPanel>
                    </CTabContent>
                  </CTabs>
                </>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </>
  )
}

export default MyProfile 